// ============================================================================
// THE DAY PAGE PROTOTYPE'S WORDS — pure functions, so a test can read every one.
//
// Nothing here reads the wall clock: "now" is the app clock (`useClock`), fixed in demo mode.
// ============================================================================

import { addDays, formatShortDate, type ISODate } from '@app/shared'

import { clockFromTimestamp, clockInstant, localDayOf } from '../../lib/time'
import type { MailTriageItem, MailVerdict } from '../../lib/data/types'

export const PILES: MailVerdict[] = ['needs_you_today', 'important', 'can_wait', 'noise']

/** The pile's name as a section heading and a button label. */
export const PILE_LABEL: Record<MailVerdict, string> = {
  needs_you_today: 'Needs you today',
  important: 'Important',
  can_wait: 'Can wait',
  noise: 'Noise',
}

/** The pile's name inside a sentence ("sorted as noise · you moved it"). */
export const PILE_WORD: Record<MailVerdict, string> = {
  needs_you_today: 'needs you today',
  important: 'important',
  can_wait: 'can wait',
  noise: 'noise',
}

export const REASON_CHIPS = [
  { reason: 'person', label: 'person' },
  { reason: 'deadline', label: 'deadline' },
  { reason: 'money', label: 'money' },
  { reason: 'not_interested', label: 'not interested' },
  { reason: 'other', label: 'other' },
] as const

/** 'kaz@school.example' → 'kaz@school': short, and still tells three inboxes apart. */
export function inboxLabel(address: string): string {
  const [local, domain = ''] = address.split('@')
  const first = domain.split('.')[0]
  return first ? `${local}@${first}` : address
}

/** The sender as a person reads it: the name, else the address. */
export function senderLabel(item: Pick<MailTriageItem, 'from'>): string {
  return item.from.name?.trim() || item.from.address
}

export function senderDomain(address: string): string {
  return address.split('@')[1]?.toLowerCase() ?? address.toLowerCase()
}

/**
 * Where the verdict came from. Never blank when there is a verdict: no AI-made sort is shown
 * without its provenance.
 */
export function provenance(item: MailTriageItem): string | null {
  if (item.ownerVerdict && item.ownerVerdict !== item.modelVerdict) {
    return item.modelVerdict
      ? `sorted as ${PILE_WORD[item.modelVerdict]} · you moved it`
      : 'you sorted it'
  }
  if (item.triageSource === 'rule') return 'your rule'
  if (item.triageSource === 'model') return `sorted by ${item.triageModel ?? 'the model'}`
  return null
}

/** '3 days' — the carry-over badge. Null for an item that surfaced today. */
export function carryOverLabel(ageDays: number): string | null {
  if (ageDays <= 0) return null
  return `${ageDays} ${ageDays === 1 ? 'day' : 'days'}`
}

/** 'due Oct 5'. */
export function deadlineLabel(deadline: ISODate): string {
  return `due ${formatShortDate(deadline)}`
}

/** '12 min ago' · '3 hours ago' · '2 days ago', from real instants (any offset on the string). */
export function agoFrom(timestamp: string, today: ISODate, nowMinutes: number): string {
  const minutes = Math.round((clockInstant(today, nowMinutes) - Date.parse(timestamp)) / 60_000)
  if (minutes < 1) return 'just now'
  if (minutes < 60) return `${minutes} min ago`
  if (minutes < 1440) {
    const h = Math.floor(minutes / 60)
    return `${h} ${h === 1 ? 'hour' : 'hours'} ago`
  }
  const d = Math.floor(minutes / 1440)
  return `${d} ${d === 1 ? 'day' : 'days'} ago`
}

/** 'sorted as of 8:32am' — with the date in front when it was not today, so stale says so. */
export function sortedAsOf(generatedAt: string, today: ISODate): string {
  const day = localDayOf(generatedAt)
  const time = clockFromTimestamp(generatedAt)
  return day === today ? `sorted as of ${time}` : `sorted as of ${formatShortDate(day)}, ${time}`
}

export interface SnoozePreset {
  label: string
  /** ISO instant, computed in this device's zone. */
  until: string
}

function localIso(date: ISODate, hour: number): string {
  const [y, m, d] = date.split('-').map(Number)
  return new Date(y!, m! - 1, d!, hour, 0).toISOString()
}

/**
 * "Tonight 6 PM" (only while it is still ahead), "Tomorrow 8 AM", "Next Monday 8 AM".
 * Next Monday is always a Monday AFTER today — on a Monday it is a week out.
 */
export function snoozePresets(today: ISODate, nowMinutes: number): SnoozePreset[] {
  const [y, m, d] = today.split('-').map(Number)
  const weekday = new Date(y!, m! - 1, d!).getDay()
  const toMonday = (1 - weekday + 7) % 7 || 7
  const out: SnoozePreset[] = []
  if (nowMinutes < 18 * 60) out.push({ label: 'Tonight 6 PM', until: localIso(today, 18) })
  out.push({ label: 'Tomorrow 8 AM', until: localIso(addDays(today, 1), 8) })
  out.push({ label: 'Next Monday 8 AM', until: localIso(addDays(today, toMonday), 8) })
  return out
}
